import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, MessageCircle, Sparkles } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { PrinterAnimation } from "@/components/PrinterAnimation";
import { DTF_PRINT_WIDTH } from "@/config/dtf";
import { whatsappLink } from "@/lib/dtf";
import { scrollToSection } from "@/lib/scroll";

const chips = [
  `Largura útil ${DTF_PRINT_WIDTH} cm`,
  "Somente DTF têxtil",
  "Cálculo instantâneo",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, -6]);

  const scrollToCalc = () => scrollToSection("#calculadora");

  return (
    <section
      ref={ref}
      id="top"
      className="relative overflow-hidden border-b border-neutral-200 bg-white pt-32 sm:pt-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-[#f58244]/15 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-12 sm:pb-16 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:px-8 lg:pb-20">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#f58244]/30 bg-[#fff4ed] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#c9561c]"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Ferramenta 100% gratuita
          </motion.span>

          <h1
            data-testid="hero-headline"
            className="mt-6 text-[clamp(2.3rem,7.2vw,4.4rem)] font-extrabold leading-[0.98] text-black"
          >
            <MaskLine delay={0.05}>Calculadora DTF Têxtil</MaskLine>
            <MaskLine delay={0.16}>
              <span className="text-[#f58244]">Online Grátis</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.32 }}
            className="mt-6 max-w-xl text-base font-medium text-neutral-800 md:text-lg"
          >
            Calcule gratuitamente quanto vai custar sua impressão DTF têxtil
            antes de fazer seu pedido.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.42 }}
            className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-500 sm:text-base"
          >
            Informe o tamanho da sua estampa e a quantidade desejada. O ClickDTF
            calcula automaticamente o espaço utilizado e o custo da impressão.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.52 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <button
              type="button"
              onClick={scrollToCalc}
              data-testid="hero-cta-calculate"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#f58244] px-7 py-4 text-base font-semibold text-white shadow-[0_16px_34px_-16px_rgba(245,130,68,1)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#e06f32]"
            >
              Calcular meu DTF têxtil
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" strokeWidth={2.6} />
            </button>
            <a
              href={whatsappLink("Olá! Vim pelo ClickDTF e gostaria de encomendar minha impressão DTF têxtil.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-black/15 bg-white px-7 py-4 text-base font-semibold text-black transition-colors duration-200 hover:border-black hover:bg-black hover:text-white"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
              Envie sua arte agora
            </a>
          </motion.div>

          <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2">
            {chips.map((c, i) => (
              <motion.li
                key={c}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.65 + i * 0.08 }}
                className="num text-[11px] uppercase tracking-[0.14em] text-neutral-500"
              >
                <span className="mr-2 text-[#f58244]">●</span>
                {c}
              </motion.li>
            ))}
          </ul>
        </div>

        <motion.div
          style={{ y, rotate }}
          className="relative hidden lg:block"
        >
          <PrinterAnimation />
        </motion.div>
      </div>
    </section>
  );
};
